import { userFacingError } from "../authorization";
import { grantKey, type GrantInput, principalKey } from "./validation";

export type GrantRemoval = {
  userGroupId?: string;
  userId?: string;
  serviceAccountId?: string;
  // Omit to remove the principal entirely; list role ids to revoke just those.
  roleIds?: string[];
};

type ExistingGrant = {
  id: string;
  userGroupId: string | null;
  userId: string | null;
  serviceAccountId?: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

// Turns add/remove deltas into the exact rows to insert and delete against a
// scope's current grants: add unions roles onto the principal (a role already
// held is skipped); remove drops the whole principal, or just its listed
// roleIds. A principal named in both add and remove is rejected as
// contradictory. Principal-generic — works for any shareable scope's grants.
export function resolveGrantDelta(
  existing: readonly ExistingGrant[],
  add: readonly GrantInput[],
  remove: readonly GrantRemoval[]
): { toInsert: GrantInput[]; toDelete: ExistingGrant[] } {
  const addPrincipals = new Set(
    add.map(principalKey).filter((k): k is string => k != null)
  );
  for (const r of remove) {
    // Removals bypass validateGrants (which enforces the add side's principal
    // XOR), so guard it here: a removal must name exactly one principal.
    const principalCount =
      (r.userGroupId ? 1 : 0) +
      (r.userId ? 1 : 0) +
      (r.serviceAccountId ? 1 : 0);
    if (principalCount !== 1)
      throw userFacingError(
        "Each removal must specify exactly one of userGroupId, userId, or serviceAccountId"
      );
    if (addPrincipals.has(principalKey(r)!))
      throw userFacingError("A principal cannot be in both add and remove");
  }

  const existingKeys = new Set(existing.map(grantKey));
  const toInsert = add.filter((g) => !existingKeys.has(grantKey(g)));

  const toDelete = existing.filter((row) => {
    const key = principalKey(row);
    if (!key) return false;
    const removal = remove.find((r) => principalKey(r) === key);
    if (!removal) return false;
    if (!removal.roleIds) return true;
    const rid = row.builtinRoleId ?? row.customRoleId;
    return rid != null && removal.roleIds.includes(rid);
  });

  return { toInsert, toDelete };
}
